import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { Upload, Users, Search, Building2, Mail, MapPin, Trash2, Plus, Loader2, Send, Printer } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useNavigate, useLocation } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerList({ token, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const customerRefs = useRef({});
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('all');
  const [showOutstanding, setShowOutstanding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  const [newCustomer, setNewCustomer] = useState({
    company_name: '',
    street: '',
    city: '',
    zipcode: '',
    country: '',
    email: '',
    contact_person: '',
    logo_data: null
  });

  const [editCustomer, setEditCustomer] = useState({
    company_name: '',
    street: '',
    city: '',
    zipcode: '',
    country: '',
    email: '',
    contact_person: '',
    logo_data: null,
    send_email_on_delivery: true,
    requires_paper_delivery_note: false
  });

  const [newCustomerLogoPreview, setNewCustomerLogoPreview] = useState(null);
  const [editCustomerLogoPreview, setEditCustomerLogoPreview] = useState(null);

  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Filter to show only one customer when returning from detail view
  useEffect(() => {
    if (location.state?.scrollToCustomerId && customers.length > 0) {
      const customerId = location.state.scrollToCustomerId;
      const customer = customers.find(c => c.id === customerId);
      
      if (customer) {
        // Set search term to show only this customer
        setSearchTerm(customer.company_name);
        setSelectedLetter('all');
        
        // Scroll to top to see the single card
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
      
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, customers]);

  useEffect(() => {
    let filtered = customers;

    // Filter by outstanding packaging
    if (showOutstanding) {
      filtered = filtered.filter(customer =>
        (customer.palette_balance || 0) > 0 ||
        (customer.eurobox_balance || 0) > 0 ||
        (customer.aufsatzrahmen_balance || 0) > 0
      );
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (customer.city && customer.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (customer.contact_person && customer.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Filter by alphabet
    if (selectedLetter !== 'all') {
      filtered = filtered.filter(customer => 
        customer.company_name.toUpperCase().startsWith(selectedLetter)
      );
    }
    
    setFilteredCustomers(filtered);
  }, [searchTerm, selectedLetter, customers, showOutstanding]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
      setFilteredCustomers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    }
  };

  const handleXMLUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/customers/import-xml`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      setUploadDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim XML-Import');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/customers`, newCustomer, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kunde erfolgreich angelegt');
      setCreateDialogOpen(false);
      setNewCustomer({
        company_name: '',
        street: '',
        city: '',
        zipcode: '',
        country: '',
        email: '',
        contact_person: '',
        logo_data: null
      });
      setNewCustomerLogoPreview(null);
      fetchCustomers();
    } catch (error) {
      // Handle Pydantic validation errors
      const detail = error.response?.data?.detail;
      let errorMessage = 'Fehler beim Anlegen des Kunden';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewCustomerLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('📤 Uploading logo:', file.name, file.type, `${(file.size / 1024).toFixed(2)} KB`);

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo-Datei ist zu groß. Maximale Größe: 5MB');
      return;
    }

    // Validate image file types
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      console.error('❌ Invalid file type:', file.type);
      toast.error(`Ungültiger Dateityp: ${file.type}. Nur JPG, PNG, GIF, SVG, WebP sind erlaubt.`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const base64String = reader.result;
        console.log('✅ Logo loaded successfully:', file.name, `${(base64String.length / 1024).toFixed(2)} KB base64`);
        setNewCustomer({ ...newCustomer, logo_data: base64String });
        setNewCustomerLogoPreview(base64String);
        toast.success(`Logo "${file.name}" erfolgreich geladen`);
      } catch (error) {
        console.error('❌ Error processing logo:', error);
        toast.error('Fehler beim Verarbeiten der Datei');
      }
    };
    reader.onerror = (error) => {
      console.error('❌ FileReader error:', error);
      toast.error('Fehler beim Laden der Datei');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveNewCustomerLogo = () => {
    setNewCustomer({ ...newCustomer, logo_data: null });
    setNewCustomerLogoPreview(null);
  };

  const handleEditCustomer = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.put(`${API}/customers/${customerToEdit.id}`, editCustomer, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Kunde erfolgreich aktualisiert');
      setEditDialogOpen(false);
      setCustomerToEdit(null);
      setEditCustomerLogoPreview(null);
      fetchCustomers();
    } catch (error) {
      // Handle Pydantic validation errors
      const detail = error.response?.data?.detail;
      let errorMessage = 'Fehler beim Aktualisieren';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCustomerLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('📤 Uploading logo:', file.name, file.type, `${(file.size / 1024).toFixed(2)} KB`);

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo-Datei ist zu groß. Maximale Größe: 5MB');
      return;
    }

    // Validate image file types
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp'];
    if (!validImageTypes.includes(file.type)) {
      console.error('❌ Invalid file type:', file.type);
      toast.error(`Ungültiger Dateityp: ${file.type}. Nur JPG, PNG, GIF, SVG, WebP sind erlaubt.`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const base64String = reader.result;
        console.log('✅ Logo loaded successfully:', file.name, `${(base64String.length / 1024).toFixed(2)} KB base64`);
        setEditCustomer({ ...editCustomer, logo_data: base64String });
        setEditCustomerLogoPreview(base64String);
        toast.success(`Logo "${file.name}" erfolgreich geladen`);
      } catch (error) {
        console.error('❌ Error processing logo:', error);
        toast.error('Fehler beim Verarbeiten der Datei');
      }
    };
    reader.onerror = (error) => {
      console.error('❌ FileReader error:', error);
      toast.error('Fehler beim Laden der Datei');
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveEditCustomerLogo = () => {
    setEditCustomer({ ...editCustomer, logo_data: null });
    setEditCustomerLogoPreview(null);
  };

  const handleOpenEditDialog = (customer) => {
    setCustomerToEdit(customer);
    setEditCustomer({
      company_name: customer.company_name,
      street: customer.street || '',
      city: customer.city || '',
      zipcode: customer.zipcode || '',
      country: customer.country || '',
      email: customer.email || '',
      contact_person: customer.contact_person || '',
      logo_data: customer.logo_data || null,
      send_email_on_delivery: customer.send_email_on_delivery !== false,
      requires_paper_delivery_note: customer.requires_paper_delivery_note === true
    });
    setEditCustomerLogoPreview(customer.logo_data || null);
    setEditDialogOpen(true);
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;

    try {
      await axios.delete(`${API}/customers/${customerToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kunde erfolgreich gelöscht');
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Kunden');
    }
  };

  const handleToggleSelectCustomer = (customerId) => {
    setSelectedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers.map(c => c.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      setIsLoading(true);
      
      // Delete all selected customers
      for (const customerId of selectedCustomers) {
        await axios.delete(`${API}/customers/${customerId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success(`${selectedCustomers.length} Kunde(n) erfolgreich gelöscht`);
      setBulkDeleteDialogOpen(false);
      setSelectedCustomers([]);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCleanupEmpty = async () => {
    try {
      const response = await axios.post(`${API}/customers/cleanup-empty`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchCustomers();
    } catch (error) {
      toast.error('Fehler beim Bereinigen');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="customer-list">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#0A1F33' }}>Kunden</h1>
            <p className="text-gray-600 mt-1">Kundendatenbank verwalten und XML importieren</p>
          </div>
          <div className="flex space-x-2">
            <Button
              variant={showOutstanding ? "default" : "outline"}
              onClick={() => setShowOutstanding(!showOutstanding)}
              className={showOutstanding ? "bg-amber-600 hover:bg-amber-700" : ""}
            >
              📦 Ausstehend
              {showOutstanding && ` (${filteredCustomers.length})`}
            </Button>
            {selectedCustomers.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setBulkDeleteDialogOpen(true)}
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {selectedCustomers.length} ausgewählt löschen
              </Button>
            )}
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="metaxon-primary text-white" data-testid="create-customer-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Kunde anlegen
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="create-customer-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Neuen Kunden anlegen</DialogTitle>
                  <DialogDescription>
                    Geben Sie die Kundendaten manuell ein
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateCustomer} className="space-y-3 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="company-name">Firmenname *</Label>
                    <Input
                      id="company-name"
                      data-testid="company-name-input"
                      value={newCustomer.company_name}
                      onChange={(e) => setNewCustomer({ ...newCustomer, company_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="street">Straße</Label>
                    <Input
                      id="street"
                      data-testid="street-input"
                      value={newCustomer.street}
                      onChange={(e) => setNewCustomer({ ...newCustomer, street: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="zipcode">PLZ</Label>
                      <Input
                        id="zipcode"
                        data-testid="zipcode-input"
                        value={newCustomer.zipcode}
                        onChange={(e) => setNewCustomer({ ...newCustomer, zipcode: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Stadt</Label>
                      <Input
                        id="city"
                        data-testid="city-input"
                        value={newCustomer.city}
                        onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Land</Label>
                    <Input
                      id="country"
                      data-testid="country-input"
                      value={newCustomer.country}
                      onChange={(e) => setNewCustomer({ ...newCustomer, country: e.target.value })}
                      placeholder="z.B. D, CH, AT"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="email-input"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-person">Ansprechpartner</Label>
                    <Input
                      id="contact-person"
                      data-testid="contact-person-input"
                      value={newCustomer.contact_person}
                      onChange={(e) => setNewCustomer({ ...newCustomer, contact_person: e.target.value })}
                      placeholder="Name des Ansprechpartners"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer-logo">Kunden-Logo (optional)</Label>
                    <Input
                      id="customer-logo"
                      type="file"
                      accept="image/*"
                      onChange={handleNewCustomerLogoUpload}
                      data-testid="customer-logo-input"
                    />
                    <p className="text-xs text-gray-500">Alle Bildformate (JPG, PNG, SVG, WebP, GIF, etc.), max. 5MB</p>
                    {newCustomerLogoPreview && (
                      <div className="mt-2 flex items-center gap-3">
                        <img
                          src={newCustomerLogoPreview}
                          alt="Logo Vorschau"
                          className="h-16 w-16 object-contain border rounded"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={handleRemoveNewCustomerLogo}
                        >
                          Logo entfernen
                        </Button>
                      </div>
                    )}
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button type="submit" className="metaxon-primary text-white" disabled={isLoading} data-testid="create-customer-submit-btn">
                      {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird erstellt...</> : 'Kunde anlegen'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="metaxon-primary text-white" data-testid="import-xml-btn">
                  <Upload className="mr-2 h-4 w-4" />
                  XML Importieren
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="import-xml-dialog">
                <DialogHeader>
                  <DialogTitle>Kunden aus XML importieren</DialogTitle>
                  <DialogDescription>
                    Laden Sie eine XML-Datei hoch, um Kundendaten zu importieren
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="xml-file">XML-Datei auswählen</Label>
                    <Input
                      id="xml-file"
                      type="file"
                      accept=".xml"
                      onChange={handleXMLUpload}
                      disabled={isLoading}
                      data-testid="xml-file-input"
                    />
                  </div>
                  {isLoading && (
                    <div className="text-sm text-gray-600">Importiere Kunden...</div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            
            <Button 
              variant="outline" 
              onClick={handleCleanupEmpty}
              data-testid="cleanup-btn"
            >
              Leere bereinigen
            </Button>
          </div>
        </div>

        <Card className="glass-effect border-0">
          <CardHeader>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Search className="h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Kunden suchen nach Firmenname, Stadt, E-Mail oder Ansprechpartner..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="customer-search-input"
                />
              </div>
              
              {/* Alphabet Filter */}
              <div className="flex flex-wrap gap-1">
                <Button
                  variant={selectedLetter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedLetter('all')}
                  className={selectedLetter === 'all' ? 'metaxon-primary text-white' : ''}
                  data-testid="filter-all"
                >
                  Alle
                </Button>
                {alphabet.map(letter => (
                  <Button
                    key={letter}
                    variant={selectedLetter === letter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedLetter(letter)}
                    className={selectedLetter === letter ? 'metaxon-primary text-white' : 'min-w-[40px]'}
                    data-testid={`filter-${letter}`}
                  >
                    {letter}
                  </Button>
                ))}
              </div>
              
              {/* Select All Checkbox */}
              {filteredCustomers.length > 0 && (
                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="select-all"
                    checked={selectedCustomers.length === filteredCustomers.length}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                  <Label htmlFor="select-all" className="text-sm font-medium">
                    Alle auswählen ({filteredCustomers.length})
                  </Label>
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCustomers.length === 0 ? (
            <Card className="col-span-full glass-effect border-0">
              <CardContent className="pt-6 text-center">
                <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">
                  {searchTerm ? 'Keine Kunden gefunden' : 'Noch keine Kunden vorhanden. Importieren Sie eine XML-Datei oder legen Sie manuell Kunden an.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredCustomers.map((customer) => (
              <Card 
                key={customer.id}
                ref={el => customerRefs.current[customer.id] = el}
                className="glass-effect border-0 hover:shadow-lg transition-shadow cursor-pointer relative overflow-hidden" 
                data-testid={`customer-card-${customer.id}`}
                onClick={() => navigate(`/customers/${customer.id}`)}
                style={customer.logo_data ? {
                  backgroundImage: `url(${customer.logo_data})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundBlendMode: 'overlay'
                } : {}}
              >
                {/* Semi-transparent overlay when logo exists */}
                {customer.logo_data && (
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `url(${customer.logo_data})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      opacity: 0.08,
                      zIndex: 0
                    }}
                  />
                )}
                <CardHeader className="relative z-10">
                  <div className="flex items-start justify-between space-x-3">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <Checkbox
                        checked={selectedCustomers.includes(customer.id)}
                        onCheckedChange={() => handleToggleSelectCustomer(customer.id)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`customer-checkbox-${customer.id}`}
                      />
                      <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
                        <Building2 className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                          {customer.company_name}
                        </CardTitle>
                        {customer.customer_id && (
                          <CardDescription className="text-xs">ID: {customer.customer_id}</CardDescription>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomerToDelete(customer);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-customer-btn-${customer.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm relative z-10">
                  {customer.contact_person && (
                    <div className="flex items-center space-x-2 font-medium text-blue-700">
                      <Users className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{customer.contact_person}</span>
                    </div>
                  )}
                  {customer.street && (
                    <div className="flex items-start space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div>{customer.street}</div>
                        <div>
                          {customer.zipcode} {customer.city}
                        </div>
                        {customer.country && <div className="text-gray-500">{customer.country}</div>}
                      </div>
                    </div>
                  )}
                  {customer.email && (
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate text-blue-600">{customer.email}</span>
                    </div>
                  )}
                  {((customer.palette_balance || 0) > 0 || (customer.eurobox_balance || 0) > 0 || (customer.aufsatzrahmen_balance || 0) > 0) && (
                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                      <strong className="text-amber-900">Verpackungsmaterial:</strong>
                      <div className="flex gap-3 mt-1">
                        {(customer.palette_balance || 0) > 0 && (
                          <span className="text-amber-800">📦 {customer.palette_balance} Paletten</span>
                        )}
                        {(customer.eurobox_balance || 0) > 0 && (
                          <span className="text-amber-800">📦 {customer.eurobox_balance} Euroboxen</span>
                        )}
                        {(customer.aufsatzrahmen_balance || 0) > 0 && (
                          <span className="text-amber-800">📦 {customer.aufsatzrahmen_balance} Aufsatzrahmen</span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="pt-2 flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditDialog(customer);
                      }}
                      data-testid={`edit-customer-btn-${customer.id}`}
                    >
                      Ändern
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {filteredCustomers.length > 0 && (
          <div className="text-center text-sm text-gray-600">
            {filteredCustomers.length} von {customers.length} Kunden angezeigt
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent data-testid="edit-customer-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Kunde ändern</DialogTitle>
            <DialogDescription>
              Aktualisieren Sie die Kundendaten
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditCustomer} className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-company-name">Firmenname *</Label>
              <Input
                id="edit-company-name"
                data-testid="edit-company-name-input"
                value={editCustomer.company_name}
                onChange={(e) => setEditCustomer({ ...editCustomer, company_name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-street">Straße</Label>
              <Input
                id="edit-street"
                data-testid="edit-street-input"
                value={editCustomer.street}
                onChange={(e) => setEditCustomer({ ...editCustomer, street: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-zipcode">PLZ</Label>
                <Input
                  id="edit-zipcode"
                  data-testid="edit-zipcode-input"
                  value={editCustomer.zipcode}
                  onChange={(e) => setEditCustomer({ ...editCustomer, zipcode: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-city">Stadt</Label>
                <Input
                  id="edit-city"
                  data-testid="edit-city-input"
                  value={editCustomer.city}
                  onChange={(e) => setEditCustomer({ ...editCustomer, city: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-country">Land</Label>
              <Input
                id="edit-country"
                data-testid="edit-country-input"
                value={editCustomer.country}
                onChange={(e) => setEditCustomer({ ...editCustomer, country: e.target.value })}
                placeholder="z.B. D, CH, AT"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">E-Mail</Label>
              <Input
                id="edit-email"
                type="email"
                data-testid="edit-email-input"
                value={editCustomer.email}
                onChange={(e) => setEditCustomer({ ...editCustomer, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-contact-person">Ansprechpartner</Label>
              <Input
                id="edit-contact-person"
                data-testid="edit-contact-person-input"
                value={editCustomer.contact_person}
                onChange={(e) => setEditCustomer({ ...editCustomer, contact_person: e.target.value })}
                placeholder="Name des Ansprechpartners"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-customer-logo">Kunden-Logo (optional)</Label>
              <Input
                id="edit-customer-logo"
                type="file"
                accept="image/*"
                onChange={handleEditCustomerLogoUpload}
                data-testid="edit-customer-logo-input"
              />
              <p className="text-xs text-gray-500">Alle Bildformate (JPG, PNG, SVG, WebP, GIF, etc.), max. 5MB</p>
              {editCustomerLogoPreview && (
                <div className="mt-2 flex items-center gap-3">
                  <img
                    src={editCustomerLogoPreview}
                    alt="Logo Vorschau"
                    className="h-16 w-16 object-contain border rounded"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={handleRemoveEditCustomerLogo}
                  >
                    Logo entfernen
                  </Button>
                </div>
              )}
            </div>

            {/* Lieferschein-Einstellungen */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-semibold text-gray-900">📄 Lieferschein-Einstellungen</h4>
              
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-start space-x-3 flex-1">
                  <Send className="h-4 w-4 text-blue-500 mt-1" />
                  <div>
                    <Label className="text-sm font-medium">E-Mail-Versand nach Unterschrift</Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Lieferschein automatisch per E-Mail verschicken
                    </p>
                  </div>
                </div>
                <Switch
                  checked={editCustomer.send_email_on_delivery}
                  onCheckedChange={(checked) => 
                    setEditCustomer({ ...editCustomer, send_email_on_delivery: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-start space-x-3 flex-1">
                  <Printer className="h-4 w-4 text-orange-500 mt-1" />
                  <div>
                    <Label className="text-sm font-medium">Papier-Lieferschein erforderlich</Label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Kunde benötigt gedruckten Lieferschein
                    </p>
                  </div>
                </div>
                <Switch
                  checked={editCustomer.requires_paper_delivery_note}
                  onCheckedChange={(checked) => 
                    setEditCustomer({ ...editCustomer, requires_paper_delivery_note: checked })
                  }
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" className="metaxon-primary text-white" disabled={isLoading} data-testid="edit-customer-submit-btn">
                {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gespeichert...</> : 'Änderungen speichern'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-customer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kunde wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Kunden "{customerToDelete?.company_name}" wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
              {customerToDelete && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800 font-medium">
                    ⚠️ Hinweis: Kunden mit Lieferscheinen können nicht gelöscht werden.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCustomer}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-customer-btn"
            >
              Kunde löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Ausgewählte Kunden löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie {selectedCustomers.length} ausgewählte Kunden wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ Hinweis: Kunden mit Lieferscheinen können nicht gelöscht werden.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-bulk-delete-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-bulk-delete-btn"
              disabled={isLoading}
            >
              {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gelöscht...</> : `${selectedCustomers.length} Kunden löschen`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
